import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { TOKEN_STORAGE_USER_KEY } from 'auth/domain/User'
import { ANALYTICS_AGENT_URL, BASE_API_URL } from 'core/infra/environment'
import { HttpClient } from 'core/infra/http/HttpClient'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

import { createAssistantBackend, type ChatBackend } from '../infra/assistantBackend'
import { ChatTokenRepository } from '../infra/ChatTokenRepository'
import { createChatTokenStore } from '../infra/chatTokenStore'
import { ThreadRepository } from '../infra/ThreadRepository'

export type AssistantServices = {
  backend: ChatBackend
  threads: Pick<ThreadRepository, 'list' | 'rename' | 'remove'>
}

export const AssistantServicesContext = createContext<AssistantServices | null>(null)

/**
 * One chat token store, shared by the chat and the thread list, for the life of
 * the page. The Opendock token is read from storage for each send, never kept.
 */
export function AssistantServicesProvider({ children }: { children: ReactNode }) {
  const services = useMemo<AssistantServices>(() => {
    const repository = new ChatTokenRepository({
      httpClient: new HttpClient(),
      baseApiUrl: BASE_API_URL,
    })
    const tokenStore = createChatTokenStore({ fetchToken: () => repository.fetch() })
    const storage = new LocalStorageClient()

    return {
      backend: createAssistantBackend({
        agentUrl: ANALYTICS_AGENT_URL,
        tokenStore,
        getOpendockToken: () => storage.get<string>(TOKEN_STORAGE_USER_KEY),
      }),
      threads: new ThreadRepository({ agentUrl: ANALYTICS_AGENT_URL, tokenStore }),
    }
  }, [])

  return (
    <AssistantServicesContext.Provider value={services}>{children}</AssistantServicesContext.Provider>
  )
}

export function useAssistantServices(): AssistantServices {
  const services = useContext(AssistantServicesContext)

  if (!services) throw new Error('useAssistantServices needs an AssistantServicesProvider')

  return services
}
